import React, { useState, useEffect } from 'react';
import './styling/QuestionNavigator.scss';

const QuestionNavigator = ({ extractedQuestions, highlightedQuestion }) => {
  // State to track which question is currently highlighted in the navigator
  const [activeId, setActiveId] = useState(null);

  // Set up intersection observer to track which questions are visible
  useEffect(() => {
    if (!extractedQuestions || Object.keys(extractedQuestions).length === 0) {
      return;
    }

    // Create a new intersection observer
    const observer = new IntersectionObserver(
      entries => {
        // Filter for entries that are intersecting (visible)
        const visibleEntries = entries.filter(entry => entry.isIntersecting);

        if (visibleEntries.length > 0) {
          // Find the one with the highest intersection ratio (most visible)
          const mostVisible = visibleEntries.reduce((prev, current) =>
            prev.intersectionRatio > current.intersectionRatio ? prev : current
          );

          // Extract the question ID from the element ID
          const id = mostVisible.target.id.replace('question-', '');

          // Update the active ID if it changed
          if (id !== activeId) {
            setActiveId(id);
            // console.log('Active question is now:', id);
          }
        }
      },
      {
        // Root is the scrollable container
        root: document.querySelector('.questions-view')?.parentElement || null,
        // Gives 0% margin around the viewport
        rootMargin: '0px',
        // Trigger when at least 15% of the target is visible
        threshold: 0.15
      }
    );

    // Observe all question cards
    const questionCards = document.querySelectorAll('.question-card');
    questionCards.forEach(card => {
      observer.observe(card);
    });

    // Clean up
    return () => {
      questionCards.forEach(card => {
        observer.unobserve(card);
      });
    };
  }, [extractedQuestions, activeId]);

  // Function to handle clicking on a question number
  const handleNavClick = id => {
    // First find the element directly using its ID
    const targetElement = document.getElementById(`question-${id}`);

    if (targetElement) {
      // Use the exact same scrollIntoView method that works elsewhere in the app
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Highlight the question briefly
      targetElement.classList.add('highlight-navigation');
      setTimeout(() => {
        targetElement.classList.remove('highlight-navigation');
      }, 1500);

      // Update the active ID
      setActiveId(id);
    }
  };

  // If no questions, don't render the navigator
  if (!extractedQuestions || Object.keys(extractedQuestions).length === 0) {
    return null;
  }

  return (
    <div className="question-navigator">
      {Object.keys(extractedQuestions).map(id => {
        // Check if this is the highlighted question
        const isHighlighted =
          activeId === id || (highlightedQuestion && highlightedQuestion.id === id);

        return (
          <button
            key={id}
            className={`nav-item ${isHighlighted ? 'highlighted' : ''}`}
            title={`Jump to Question ${id}`}
            onClick={() => handleNavClick(id)}
          >
            {id}
          </button>
        );
      })}
    </div>
  );
};

export default QuestionNavigator;
